/**
 * Nummernkreise — Einstellungen (Pro-Feature, Juni 2026).
 *
 * Pro Belegkategorie (Rechnungen, Angebote, Gutschriften/Stornos) kann der User
 * zwischen dem ivoliq-Standardschema und einem eigenen Nummernkreis wählen
 * (Muster, Startwert, Reset-Intervall) — mit Live-Vorschau und Konflikt-Schutz.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Hash, Lock, Loader2, Sparkles } from 'lucide-react';

const RESET_OPTIONS = [
  { value: 'yearly', label: 'Jährlich (Zähler startet jedes Jahr neu)' },
  { value: 'monthly', label: 'Monatlich (Zähler startet jeden Monat neu)' },
  { value: 'never', label: 'Nie (durchgehend fortlaufend)' },
];

export default function NumberRangesSection({ onlyCategories = null }) {
  const [ranges, setRanges] = useState([]);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});       // category → { pattern, start_value, reset_interval, preview }
  const [savingCat, setSavingCat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/number-ranges');
      setRanges(res.data.ranges || []);
      setIsPro(!!res.data.is_pro);
      const d = {};
      (res.data.ranges || []).forEach((r) => {
        d[r.category] = {
          pattern: r.pattern,
          start_value: r.start_value,
          reset_interval: r.reset_interval || 'yearly',
          preview: r.preview,
        };
      });
      setDrafts(d);
    } catch {
      toast.error('Nummernkreise konnten nicht geladen werden.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setMode = async (category, mode) => {
    if (mode === 'custom' && !isPro) {
      toast.error('Eigene Nummernkreise sind ein Pro-Feature.');
      return;
    }
    if (mode === 'default') {
      setSavingCat(category);
      try {
        await api.put(`/number-ranges/${category}`, { mode: 'default' });
        toast.success('Auf ivoliq-Standard zurückgesetzt.');
        await load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
      } finally { setSavingCat(null); }
      return;
    }
    // custom: optimistisch in Edit-Modus wechseln (noch nicht speichern)
    setRanges((prev) => prev.map((r) => (r.category === category ? { ...r, mode: 'custom' } : r)));
  };

  const updateDraft = (category, patch) => {
    setDrafts((prev) => ({ ...prev, [category]: { ...prev[category], ...patch } }));
  };

  const refreshPreview = useCallback(async (category, draft) => {
    try {
      const res = await api.get('/number-ranges/preview', {
        params: { pattern: draft.pattern, start_value: Number(draft.start_value) || 1 },
      });
      updateDraft(category, { preview: res.data.preview, previewError: null });
    } catch (e) {
      updateDraft(category, { preview: null, previewError: e.response?.data?.detail || 'Ungültiges Muster' });
    }
  }, []);

  const saveCustom = async (category) => {
    const d = drafts[category];
    setSavingCat(category);
    try {
      const res = await api.put(`/number-ranges/${category}`, {
        mode: 'custom',
        pattern: d.pattern,
        start_value: Number(d.start_value) || 1,
        reset_interval: d.reset_interval,
      });
      toast.success(`Nummernkreis gespeichert — nächste Nummer: ${res.data.preview}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSavingCat(null); }
  };

  if (loading) {
    return (
      <Card data-testid="number-ranges-section">
        <CardContent className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="number-ranges-section" id="section-number-ranges">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hash className="h-5 w-5 text-emerald-600" />
          Nummernkreise
          {!isPro && <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1"><Lock className="h-3 w-3" /> Pro</Badge>}
        </CardTitle>
        <CardDescription>
          Lege je Belegart fest, ob ivoliq die Nummern vergibt oder du einen eigenen Nummernkreis nutzt.
          Eigene Nummernkreise sind ein <strong>Pro-Feature</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {ranges.filter((r) => !onlyCategories || onlyCategories.includes(r.category)).map((r) => {
          const d = drafts[r.category] || {};
          const isCustom = r.mode === 'custom';
          return (
            <div key={r.category} className="rounded-xl border border-slate-200 p-4" data-testid={`number-range-${r.category}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-semibold text-slate-800">{r.label}</div>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid={`number-range-mode-${r.category}`}>
                  <button
                    onClick={() => setMode(r.category, 'default')}
                    disabled={savingCat === r.category}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${!isCustom ? 'bg-white shadow text-emerald-700' : 'text-slate-500'}`}
                    data-testid={`number-range-default-${r.category}`}
                  >
                    ivoliq-Standard
                  </button>
                  <button
                    onClick={() => setMode(r.category, 'custom')}
                    disabled={savingCat === r.category || !isPro}
                    title={!isPro ? 'Pro-Feature' : ''}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${isCustom ? 'bg-white shadow text-emerald-700' : 'text-slate-500'} ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`number-range-custom-${r.category}`}
                  >
                    {!isPro && <Lock className="h-3 w-3" />} Eigener Kreis
                  </button>
                </div>
              </div>

              {!isCustom ? (
                <p className="text-sm text-slate-500 mt-2" data-testid={`number-range-preview-${r.category}`}>
                  Nächste Nummer: <span className="font-mono font-semibold text-slate-700">{r.preview}</span>
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Muster</Label>
                      <Input
                        value={d.pattern || ''}
                        onChange={(e) => updateDraft(r.category, { pattern: e.target.value })}
                        onBlur={() => refreshPreview(r.category, drafts[r.category])}
                        placeholder="RE-{YYYY}-{NNNN}"
                        className="font-mono"
                        data-testid={`number-range-pattern-${r.category}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Startwert</Label>
                      <Input
                        type="number" min="1"
                        value={d.start_value ?? 1}
                        onChange={(e) => updateDraft(r.category, { start_value: e.target.value })}
                        onBlur={() => refreshPreview(r.category, drafts[r.category])}
                        data-testid={`number-range-start-${r.category}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Reset-Intervall</Label>
                    <Select value={d.reset_interval || 'yearly'} onValueChange={(v) => updateDraft(r.category, { reset_interval: v })}>
                      <SelectTrigger data-testid={`number-range-reset-${r.category}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RESET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-slate-400">
                    Platzhalter: <code>{'{YYYY}'}</code> Jahr · <code>{'{YY}'}</code> Kurzjahr · <code>{'{MM}'}</code> Monat ·{' '}
                    <code>{'{NNNN}'}</code> laufende Nummer (Anzahl <code>N</code> = Stellen)
                  </p>
                  <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                    <span className="text-sm" data-testid={`number-range-preview-${r.category}`}>
                      {d.previewError
                        ? <span className="text-red-600">{d.previewError}</span>
                        : <>Vorschau: <span className="font-mono font-semibold text-emerald-700">{d.preview || r.preview}</span></>}
                    </span>
                    <Button size="sm" onClick={() => saveCustom(r.category)} disabled={savingCat === r.category} data-testid={`number-range-save-${r.category}`}>
                      {savingCat === r.category ? 'Speichert…' : 'Nummernkreis speichern'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!isPro && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-center gap-2 text-sm text-amber-800" data-testid="number-ranges-pro-hint">
            <Sparkles className="h-4 w-4 flex-shrink-0" />
            Eigene Nummernkreise (z.B. <code className="font-mono">RE-2026-0001</code>) gibt es im Pro-Plan.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
